import { LoginUser } from 'types/api';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { subscribeUI, closeMobileSidebar, setSidebarCollapsed } from 'utils/uiStore';
import Icon from '../AppIcon';

interface NavItem {
  name: string;
  path: string;
  icon: string;
}

// Function to get current user role
const getUserRole = (): string => {
  const userJson = localStorage.getItem('user');
  if (!userJson) return '';

  try {
    const user: LoginUser = JSON.parse(userJson);
    return user.role;
  } catch {
    return '';
  }
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const location = useLocation();

  useEffect(() => {
    // Get user role on mount and when location changes
    setUserRole(getUserRole());
  }, [location.pathname]);

  // Conditional navigation items based on user role
  const getNavItems = (): NavItem[] => {
    if (userRole === 'super_admin') {
      return [
        { name: 'Dashboard', path: '/admin/dashboard', icon: 'LayoutDashboard' },
        { name: 'Accounts', path: '/admin/accounts', icon: 'Users' },
        { name: 'AI Usage', path: '/admin/ai-usage', icon: 'Cpu' },
        { name: 'Logs', path: '/admin/logs', icon: 'FileText' },
        { name: 'User Journey', path: '/admin/user-journey', icon: 'Footprints' },
        { name: 'Data', path: '/admin/data', icon: 'Database' },
        { name: 'Tours', path: '/admin/tours', icon: 'MapPin' },
        { name: 'App Settings', path: '/admin/mcp', icon: 'Settings' },
      ];
    } else {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
        { name: 'Reservation', path: '/reservation', icon: 'Calendar' },
        { name: 'Forecasting', path: '/ai-analysis', icon: 'Brain' },
        { name: 'Report', path: '/report', icon: 'BarChart3' },
      ];
    }
  };

  // Subscribe to global UI store for mobile sidebar open/close
  useEffect(() => {
    const unsub = subscribeUI(s => {
      setMobileOpen(s.mobileSidebarOpen);
      // Always sync without referencing stale closure variable
      setCollapsed(s.sidebarCollapsed);
    });
    return () => unsub();
  }, []);

  // Lock body scroll when mobile sidebar open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [mobileOpen]);

  return (
    <>
      {/* Backdrop for mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}
      <aside
        className={`bg-white border-r h-screen pt-16 transition-all duration-300 ${collapsed ? 'w-12' : 'w-24'} flex flex-col
        fixed left-0 top-0 z-50 overflow-y-auto
        md:translate-x-0 md:opacity-100
        ${mobileOpen ? 'translate-x-0 opacity-100 shadow-xl' : '-translate-x-full opacity-0'}
        md:shadow-none
        `}
        aria-label="Sidebar navigation"
      >
        {/* Navigation items */}
        <nav data-tour="sidebar-nav" className="flex-1 mt-2 px-0 space-y-2">
          {getNavItems().map(item => {
            const active = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.name}
                to={item.path}
                title={item.name}
                className={`group relative flex flex-col items-center justify-center rounded-md mx-1 py-3 text-[11px] font-medium tracking-wide transition-colors duration-200 border overflow-hidden
                ${active ? 'bg-gray-50 border-primary text-primary' : 'border-transparent text-gray-600 hover:bg-gray-100'}
                ${collapsed ? 'py-2' : ''}`}
                onClick={() => {
                  if (mobileOpen) closeMobileSidebar();
                }}
              >
                <Icon
                  name={item.icon}
                  size={collapsed ? 18 : 26}
                  className={`${active ? 'text-primary' : 'text-gray-500 group-hover:text-gray-700'} transition-all duration-200`}
                />
                <span
                  className={`mt-2 leading-tight text-center whitespace-nowrap transition-all duration-200 ease-out
                  ${collapsed ? 'opacity-0 translate-y-1 pointer-events-none' : 'opacity-100 translate-y-0'}`}
                >
                  {item.name}
                </span>
                {collapsed && (
                  <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 py-1 px-2 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 pointer-events-none shadow-lg whitespace-nowrap transition-opacity duration-200 z-50">
                    {item.name}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom control bar with collapse toggle */}
        <div className="border-t p-2 flex items-center justify-center gap-2">
          {/* Close button (mobile) */}
          <button
            onClick={closeMobileSidebar}
            className={`p-2 rounded hover:bg-gray-100 focus:outline-none text-gray-500 md:hidden ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            aria-label="Close menu"
          >
            <Icon name="X" size={18} />
          </button>
          <button
            onClick={() => {
              if (mobileOpen) {
                // In mobile just ensure expanded (labels visible)
                setSidebarCollapsed(false);
                return;
              }
              // Desktop toggle: if currently collapsed -> expand, else collapse
              setSidebarCollapsed(!collapsed);
            }}
            className="p-2 rounded hover:bg-gray-100 focus:outline-none text-gray-600"
            aria-label="Toggle sidebar"
          >
            <Icon name={collapsed ? 'ChevronRight' : 'ChevronLeft'} size={18} />
          </button>
        </div>
      </aside>
    </>
  );
}
